import type { LatLng } from '@talkofthetown/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChosenPlace } from '../lib/places';

export type LocationStatus = 'idle' | 'locating' | 'granted' | 'denied' | 'unavailable';

export interface ViewerLocation {
  /** The device location's status (not needed while a chosen place is in use). */
  status: LocationStatus;
  /**
   * Where distances are measured from: the chosen place, else the device location (rounded to
   * ~1 km). Never leaves the device: only used to pick which area boards to read.
   */
  center: LatLng | null;
  /** The place the reader picked, or null when following their own location. */
  place: ChosenPlace | null;
  /** Follow the device location (asks for it when needed). */
  request: () => void;
  /** Look around a chosen place instead of the device location. */
  choose: (place: ChosenPlace) => void;
}

const LOCATION_KEY = 'tott.location';
const PLACE_KEY = 'tott.place';
const round = (x: number) => Math.round(x * 100) / 100;

function load<T>(key: string, valid: (v: T) => boolean): T | null {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? 'null') as T | null;
    return v && valid(v) ? v : null;
  } catch {
    return null;
  }
}

function save(key: string, value: unknown) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode: fine, we just won't remember */
  }
}

const isLatLng = (v: LatLng) => Number.isFinite(v.lat) && Number.isFinite(v.lng);
const rememberedLocation = () => load<LatLng>(LOCATION_KEY, isLatLng);
const rememberedPlace = () =>
  load<ChosenPlace>(PLACE_KEY, (v) => typeof v.name === 'string' && isLatLng(v));

export function useViewerLocation(): ViewerLocation {
  const [place, setPlace] = useState<ChosenPlace | null>(rememberedPlace);
  const [device, setDevice] = useState<LatLng | null>(rememberedLocation);
  const [status, setStatus] = useState<LocationStatus>(() =>
    rememberedLocation() ? 'granted' : 'idle',
  );

  const locate = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }
    setStatus((s) => (s === 'granted' ? s : 'locating'));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: round(pos.coords.latitude), lng: round(pos.coords.longitude) };
        setDevice(c);
        setStatus('granted');
        save(LOCATION_KEY, c);
      },
      (err) => {
        setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
        if (err.code === err.PERMISSION_DENIED) {
          setDevice(null);
          save(LOCATION_KEY, null);
        }
      },
      { enableHighAccuracy: false, timeout: 15_000, maximumAge: 10 * 60_000 },
    );
  }, []);

  const request = useCallback(() => {
    setPlace(null);
    save(PLACE_KEY, null);
    locate();
  }, [locate]);

  const choose = useCallback((p: ChosenPlace) => {
    const chosen = { name: p.name, detail: p.detail, lat: round(p.lat), lng: round(p.lng) };
    setPlace(chosen);
    save(PLACE_KEY, chosen);
  }, []);

  // If the visitor already allowed location before (and follows it), refresh it quietly;
  // otherwise wait for a click.
  useEffect(() => {
    let cancelled = false;
    const following = !rememberedPlace();
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((p) => {
        if (cancelled) return;
        if (p.state === 'granted' && following) locate();
        else if (p.state === 'denied') setStatus('denied');
      })
      .catch(() => {
        if (!cancelled && following && rememberedLocation()) locate();
      });
    return () => {
      cancelled = true;
    };
  }, [locate]);

  const center = useMemo(
    () => (place ? { lat: place.lat, lng: place.lng } : device),
    [place, device],
  );
  return { status, center, place, request, choose };
}
